import { getConfig } from '@sydle/ui-utils';
import { ColorHelper } from '../utils/color-helpers';
import type { DesignSystemConfig } from '../utils/types/design-system-config.types';

/**
 * Insert style tokens into html body using package definitions
 */
function setGlobalTokens(): void {
  const designSystemConfig = getConfig<DesignSystemConfig>('ui-design-system');

  if (!designSystemConfig) {
    return;
  }

  const primaryColor = designSystemConfig.primaryColor;
  const secondaryColor = designSystemConfig.secondaryColor;

  let style: { [key: string]: string } = {};

  if (primaryColor) {
    style = { ...style, '--sy-primary': primaryColor };
    const variants = ColorHelper.getColorVariations(primaryColor);
    variants.forEach((variant: { name: string; value: string }) => {
      style = { ...style, [`--sy-primary-${variant.name}`]: variant.value };
    });
  }

  if (secondaryColor) {
    style = { ...style, '--sy-secondary': secondaryColor };
    const variants = ColorHelper.getColorVariations(secondaryColor);
    variants.forEach((variant: { name: string; value: string }) => {
      style = { ...style, [`--sy-secondary-${variant.name}`]: variant.value };
    });
  }

  const surfaceVariations = ColorHelper.getSurfaceColorVariations(primaryColor);

  const calculateSurfaceTokens = (surfaceType: string, primaryColor: string, defaultColor = '#ffffff') => {
    const variations = ColorHelper.getColorVariations(defaultColor, primaryColor, 0.4);
    const hover = variations.find((variation) => variation.name === 'hover')?.value || defaultColor;
    const active = variations.find((variation) => variation.name === 'active')?.value || defaultColor;
    const colorType = surfaceType ? `-${surfaceType}` : '';
    style = {
      ...style,
      [`--sy-surface${colorType}`]: defaultColor,
      [`--sy-surface${colorType}-hover`]: hover,
      [`--sy-surface${colorType}-active`]: active,
    };
  };

  calculateSurfaceTokens('', primaryColor);

  const surfaceLight = surfaceVariations.find((surface) => surface.name === 'light');
  if (surfaceLight) {
    calculateSurfaceTokens(surfaceLight.name, primaryColor, surfaceLight.value);
  }

  const surfaceDark = surfaceVariations.find((surface) => surface.name === 'dark');
  if (surfaceDark) {
    calculateSurfaceTokens(surfaceDark.name, primaryColor, surfaceDark.value);
  }

  calculateSurfaceTokens('elevated', primaryColor);

  const styleElement = document.createElement('style');
  styleElement.innerHTML = `:root ${JSON.stringify(style)?.replace(/","/g, '";"').replace(/"/g, '')}`;
  document.head.appendChild(styleElement);
}

setGlobalTokens();
